import { FC, useState, useMemo } from 'react';
import { useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import {
  ContainerChargeDto,
  DeclarationChargeDto,
  ShipmentChargeDto,
  WeightChargeDto,
  PercentageChargeDto,
  ContractChargeListsDto,
} from 'src/generated/services/TFinancialApi';

import { beautifyEnum } from 'src/utils/utility';
import { formatDate } from 'src/utils/date';
import { SelectDropdown } from '../../contractManager/filter/SelectDropdown';
import { GroupedShippingService } from './types';

const StyledBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  color: '#9F9F9F',
  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#9F9F9F',
    fontSize: '0.75rem',
    top: '3px',
    '@media (min-width: 1720px)': {
      fontSize: '16px',
    },
  },
  '& .MuiFormControl-root .MuiSelect-select': {
    fontSize: '13px',
    '@media (min-width: 1720px)': {
      fontSize: '16px',
    },
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    borderRadius: '6px',
    borderColor: '#DFDFDF',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#DFDFDF',
  },
});

// Union type for shipment and declaration charges (they have the same structure)
type ShipmentDeclarationChargeDto = ShipmentChargeDto | DeclarationChargeDto;

type UnifiedChargeData = ContainerChargeDto | ShipmentDeclarationChargeDto | WeightChargeDto | PercentageChargeDto;

interface ITableChargesUnifiedProps {
  data: ContractChargeListsDto;
  selectedGroup: GroupedShippingService;
  selectedVariantIndex: number;
  onVariantChange: (index: number) => void;
  onClose: () => void;
}

const chargeCategoryOptions = [
  { value: 'declaration', label: 'Declaration' },
  { value: 'shipment', label: 'Shipment' },
  { value: 'container', label: 'Container' },
  { value: 'weight', label: 'Weight' },
  { value: 'percentage', label: 'Percentage' },
];

export const TableChargesUnified: FC<ITableChargesUnifiedProps> = ({ data, selectedGroup, selectedVariantIndex, onVariantChange, onClose }) => {
  const [chargeCategory, setChargeCategory] = useState('shipment');

  // Create validity period options from selected group
  const validityPeriodOptions = useMemo(
    () =>
      selectedGroup.variants.map((variant, index) => {
        // Check if validity period is mismatched (startDate, endDate, or "validity period")
        const hasMismatch = variant.mismatchedFields?.some((field) => field === 'validity period' || field === 'startDate' || field === 'endDate');
        const labelText = `${formatDate(variant.validityPeriod.start)} to ${formatDate(variant.validityPeriod.end)}`;

        return {
          value: index.toString(),
          // Wrap label in span with red color if mismatched
          label: hasMismatch ? <span style={{ color: 'red' }}>{labelText}</span> : labelText,
        };
      }),
    [selectedGroup.variants]
  );

  // Type guards for safe type checking
  const isContainerCharge = (charge: UnifiedChargeData): charge is ContainerChargeDto => 'unitPrice20' in charge || 'unitPrice40' in charge;

  const isShipmentCharge = (charge: UnifiedChargeData): charge is ShipmentDeclarationChargeDto =>
    'unitPrice' in charge && !('unitPrice20' in charge) && !('value' in charge) && !('chargeUnit' in charge);

  const isWeightCharge = (charge: UnifiedChargeData): charge is WeightChargeDto => 'chargeUnit' in charge;

  const isPercentageCharge = (charge: UnifiedChargeData): charge is PercentageChargeDto => 'value' in charge;

  // Merge all charges into a single array with type indicator
  const unifiedData = useMemo((): UnifiedChargeData[] => {
    const charges: UnifiedChargeData[] = [];

    if (chargeCategory === 'declaration' && data?.declarationCharges) {
      charges.push(...data.declarationCharges);
    }
    if (chargeCategory === 'shipment' && data?.shipmentCharges) {
      charges.push(...data.shipmentCharges);
    }
    if (chargeCategory === 'container' && data?.containerCharges) {
      charges.push(...data.containerCharges);
    }
    if (chargeCategory === 'weight' && data?.weightCharges) {
      charges.push(...data.weightCharges);
    }
    if (chargeCategory === 'percentage' && data?.percentageCharges) {
      charges.push(...data.percentageCharges);
    }

    return charges;
  }, [data, chargeCategory]);

  // Common prefix columns (appear first in all categories)
  const getCommonPrefixColumns = (): MRT_ColumnDef<UnifiedChargeData>[] => [
    {
      accessorKey: 'chargeName',
      header: 'Charge Name',
      enableEditing: false,
      size: 200,
      Cell: ({ row }) => <span className='text-12 xl:text-14'>{row.original?.chargeName ? beautifyEnum(row.original.chargeName).toUpperCase() : '-'}</span>,
    },
    {
      accessorKey: 'chargeCategory',
      header: 'Charge Category',
      enableEditing: false,
      size: 180,
      Cell: ({ row }) => (
        <span className='text-12 xl:text-14'>{row.original?.chargeCategory ? beautifyEnum(row.original.chargeCategory).toUpperCase() : '-'}</span>
      ),
    },
    {
      accessorKey: 'legType',
      header: 'Leg Type',
      enableEditing: false,
      size: 150,
      Cell: ({ row }) => <span className='text-12 xl:text-14'>{row.original?.legType ? beautifyEnum(row.original.legType).toUpperCase() : '-'}</span>,
    },
  ];

  // Common suffix columns (appear last in all categories)
  const getCommonSuffixColumns = (): MRT_ColumnDef<UnifiedChargeData>[] => [
    {
      accessorKey: 'accessorial',
      header: 'Accessorial',
      enableEditing: false,
      size: 120,
      Cell: ({ row }) => {
        if (row.original?.accessorial === undefined) return <span className='text-14 xl:text-16'>-</span>;
        return <span className='text-14 xl:text-16'>{row.original.accessorial ? 'True' : 'False'}</span>;
      },
    },
    {
      accessorKey: 'approval',
      header: 'Approval',
      enableEditing: false,
      size: 120,
      Cell: ({ row }) => {
        if (row.original?.approval === undefined) return <span className='text-14 xl:text-16'>-</span>;
        return <span className='text-14 xl:text-16'>{row.original.approval ? 'True' : 'False'}</span>;
      },
    },
    {
      accessorKey: 'outlay',
      header: 'Outlay',
      enableEditing: false,
      size: 120,
      Cell: ({ row }) => {
        if (row.original?.outlay === undefined) return <span className='text-14 xl:text-16'>-</span>;
        return <span className='text-14 xl:text-16'>{row.original.outlay ? 'True' : 'False'}</span>;
      },
    },
  ];

  // Category-specific columns for Shipment charges
  const getShipmentSpecificColumns = (): MRT_ColumnDef<UnifiedChargeData>[] => [
    {
      accessorKey: 'unitPrice',
      header: 'Unit Price',
      enableEditing: false,
      size: 150,
      Cell: ({ row }) => {
        if (isShipmentCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.unitPrice ?? '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'currency',
      header: 'Currency',
      enableEditing: false,
      size: 120,
      Cell: ({ row }) => {
        if (isShipmentCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.currency || '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
  ];

  // Category-specific columns for Container charges
  const getContainerSpecificColumns = (): MRT_ColumnDef<UnifiedChargeData>[] => [
    {
      accessorKey: 'unitPrice20',
      header: 'Unit Price 20',
      enableEditing: false,
      size: 150,
      Cell: ({ row }) => {
        if (isContainerCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.unitPrice20 ?? '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'unitPrice40',
      header: 'Unit Price 40',
      enableEditing: false,
      size: 150,
      Cell: ({ row }) => {
        if (isContainerCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.unitPrice40 ?? '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'currency',
      header: 'Currency',
      enableEditing: false,
      size: 120,
      Cell: ({ row }) => {
        if (isContainerCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.currency || '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'containerType',
      header: 'Container Type',
      enableEditing: false,
      size: 150,
      Cell: ({ row }) => {
        if (isContainerCharge(row.original)) {
          return <span className='capitalize text-14 xl:text-16'>{row.original.containerType ? beautifyEnum(row.original.containerType) : '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
  ];

  // Category-specific columns for Weight charges
  const getWeightSpecificColumns = (): MRT_ColumnDef<UnifiedChargeData>[] => [
    {
      accessorKey: 'minimumPrice',
      header: 'Minimum Price',
      enableEditing: false,
      size: 150,
      Cell: ({ row }) => {
        if (isWeightCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.minimumPrice ?? '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'unitPrice',
      header: 'Unit Price',
      enableEditing: false,
      size: 150,
      Cell: ({ row }) => {
        if (isWeightCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.unitPrice ?? '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'currency',
      header: 'Currency',
      enableEditing: false,
      size: 120,
      Cell: ({ row }) => {
        if (isWeightCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.currency || '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'chargeUnit',
      header: 'Charge Unit',
      enableEditing: false,
      size: 150,
      Cell: ({ row }) => {
        if (isWeightCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.chargeUnit || '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'calculationMethod',
      header: 'Calculation Method',
      enableEditing: false,
      size: 180,
      Cell: ({ row }) => {
        if (isWeightCharge(row.original)) {
          return <span className='capitalize text-14 xl:text-16'>{row.original.calculationMethod ? beautifyEnum(row.original.calculationMethod) : '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'minimumChargeableWeight',
      header: 'Minimum Range',
      enableEditing: false,
      size: 150,
      Cell: ({ row }) => {
        if (isWeightCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.minimumChargeableWeight ?? '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'maximumChargeableWeight',
      header: 'Maximum Range',
      enableEditing: false,
      size: 150,
      Cell: ({ row }) => {
        if (isWeightCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.maximumChargeableWeight ?? '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'includedQuantity',
      header: 'Included Quantity',
      enableEditing: false,
      size: 170,
      Cell: ({ row }) => {
        if (isWeightCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.includedQuantity ?? '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'startDate',
      header: 'Validity Start Date',
      enableEditing: false,
      size: 170,
      Cell: ({ row }) => {
        if (isWeightCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.startDate ? formatDate(new Date(row.original.startDate)) : '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'endDate',
      header: 'Validity End Date',
      enableEditing: false,
      size: 170,
      Cell: ({ row }) => {
        if (isWeightCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.endDate ? formatDate(new Date(row.original.endDate)) : '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
  ];

  // Category-specific columns for Percentage charges
  const getPercentageSpecificColumns = (): MRT_ColumnDef<UnifiedChargeData>[] => [
    {
      accessorKey: 'value',
      header: 'Rate',
      enableEditing: false,
      size: 120,
      Cell: ({ row }) => {
        if (isPercentageCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.value ? `${row.original.value}%` : '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'currency',
      header: 'Currency',
      enableEditing: false,
      size: 120,
      Cell: ({ row }) => {
        if (isPercentageCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.currency || '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'minimumPrice',
      header: 'Minimum Price',
      enableEditing: false,
      size: 150,
      Cell: ({ row }) => {
        if (isPercentageCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.minimumPrice ?? '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'eligibleChargeDomain',
      header: 'Eligible Charge Category',
      enableEditing: false,
      size: 200,
      Cell: ({ row }) => {
        if (isPercentageCharge(row.original)) {
          return (
            <span className='capitalize text-14 xl:text-16'>{row.original.eligibleChargeDomain ? beautifyEnum(row.original.eligibleChargeDomain) : '-'}</span>
          );
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'eligibleChargeSubDomain',
      header: 'Eligible Charge Name',
      enableEditing: false,
      size: 200,
      Cell: ({ row }) => {
        if (isPercentageCharge(row.original)) {
          return (
            <span className='capitalize text-14 xl:text-16'>
              {row.original.eligibleChargeSubDomain ? beautifyEnum(row.original.eligibleChargeSubDomain) : '-'}
            </span>
          );
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'startDate',
      header: 'Validity Start Date',
      enableEditing: false,
      size: 170,
      Cell: ({ row }) => {
        if (isPercentageCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.startDate ? formatDate(new Date(row.original.startDate)) : '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
    {
      accessorKey: 'endDate',
      header: 'Validity End Date',
      enableEditing: false,
      size: 170,
      Cell: ({ row }) => {
        if (isPercentageCharge(row.original)) {
          return <span className='text-14 xl:text-16'>{row.original.endDate ? formatDate(new Date(row.original.endDate)) : '-'}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
    },
  ];

  // Build columns dynamically based on selected charge category
  const buildColumns = (category: string): MRT_ColumnDef<UnifiedChargeData>[] => {
    const columns: MRT_ColumnDef<UnifiedChargeData>[] = [];

    // 1. Add common prefix columns (always first)
    columns.push(...getCommonPrefixColumns());

    // 2. Add category-specific columns (middle section)
    switch (category) {
      case 'shipment':
        columns.push(...getShipmentSpecificColumns());
        break;
      case 'container':
        columns.push(...getContainerSpecificColumns());
        break;
      case 'weight':
        columns.push(...getWeightSpecificColumns());
        break;
      case 'percentage':
        columns.push(...getPercentageSpecificColumns());
        break;
      default:
        break;
    }

    // 3. Add common suffix columns (always last)
    columns.push(...getCommonSuffixColumns());

    return columns;
  };

  // Dynamic columns based on charge category
  const columns = useMemo<MRT_ColumnDef<UnifiedChargeData>[]>(() => buildColumns(chargeCategory), [chargeCategory]);

  const table = useMaterialReactTable({
    data: unifiedData,
    columns,
    enableRowNumbers: true,
    enableSorting: true,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: false,
    muiTableProps: {
      sx: {
        borderCollapse: 'separate',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: '#F5F6FA',
        fontWeight: 600,
        fontSize: '14px',
        color: '#4A4A4A',
        textTransform: 'uppercase',
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        padding: '12px 16px',
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    muiTableBodyRowProps: {
      sx: {
        '&:hover': {
          backgroundColor: 'rgb(242, 242, 242)!important',
        },
      },
    },
  });

  return (
    <div className='h-full bg-white px-24'>
      <div className='flex flex-col h-full'>
        <div className='flex items-center py-12 '>
          <div className='flex-1 flex items-center'>
            <h3 className='text-14 xl:text-16 text-neutral-50 font-medium overflow-hidden'>CHARGES DETAILS</h3>
          </div>
          <div className='flex items-center gap-12'>
            <StyledBox>
              <SelectDropdown
                label='Charge Type'
                options={chargeCategoryOptions}
                value={chargeCategory}
                setValue={(value: string) => setChargeCategory(value)}
                urlParamName='category'
              />
            </StyledBox>
            <StyledBox>
              <div
                style={{
                  color: selectedGroup.variants[selectedVariantIndex]?.mismatchedFields?.some(
                    (field) => field === 'validity period' || field === 'startDate' || field === 'endDate'
                  )
                    ? 'red'
                    : 'inherit',
                }}
              >
                <SelectDropdown
                  label='Validity Period'
                  options={validityPeriodOptions}
                  value={selectedVariantIndex.toString()}
                  setValue={(value: string) => onVariantChange(parseInt(value, 10))}
                  urlParamName='validityPeriod'
                />
              </div>
            </StyledBox>
            <button type='button' aria-label='Close' className='ml-auto text-neutral-60' onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className='flex-1 overflow-auto'>
          <CustomTable table={table} />
        </div>
      </div>
    </div>
  );
};
